import React, { useState } from "react";
import { FaSortUp, FaSortDown, FaChevronRight } from "react-icons/fa";
import { ListView, ListSkeleton, EmptyState } from "../shared";
import { Pagination } from "../atoms";
import { useGetTasksQuery } from "../../store";
import StatusBadge from "./atoms/StatusBadge";
import PriorityIcon from "./atoms/PriorityIcon";
import AssigneeChips from "./AssigneeChips";
import useQuickAssign from "./useQuickAssign";
import { ParentKey, TaskCounts } from "./atoms/TaskCounts";
import OverdueBadge from "./atoms/OverdueBadge";
import { isOverdue, formatDay, formatDate } from "./taskConstants";

const PAGE_SIZE = 25;

// label: başlık; field: server sort alanı (yoksa sıralanamaz)
const COLUMNS = [
  { key: "key", label: "Anahtar", field: "key" },
  { key: "title", label: "Başlık", field: "title" },
  { key: "status", label: "Durum", field: "status" },
  { key: "priority", label: "Öncelik", field: "priority" },
  { key: "assignee", label: "Atanan" },
  { key: "dueDate", label: "Bitiş", field: "dueDate" },
  { key: "updatedAt", label: "Güncellenme", field: "updatedAt" },
];


function DueDate({ task }) {
  const overdue = isOverdue(task);
  return (
    <span className={`inline-flex items-center gap-1.5 ${overdue ? "text-red-700 font-Medium" : "text-gray-600"}`}>
      {formatDay(task.dueDate) || "—"}
      {overdue && <OverdueBadge />}
    </span>
  );
}

// Bitişi geçmiş ve bitmemiş iş: satır/kart kırmızı yanıp söner (iş bitince durur).
const bgFor = (t, normal) => (isOverdue(t) ? "animate-staleBlink" : normal);

// Alt iş / kontrol listesi sayaçları (toplam > 0 ise)
function TaskMeta({ task }) {
  const has = task.subtaskCount?.total > 0 || task.checklistCount?.total > 0;
  if (!has) return null;
  return (
    <div className="flex items-center gap-3 mt-0.5 text-xs text-gray-500">
      <TaskCounts task={task} />
    </div>
  );
}

// 1.6 — ağaç: ▸/▾ düğmesi (yalnız açılabilir satırda); satır tıklamasını tetiklemez.
function ExpandToggle({ task, expanded, onToggle }) {
  if (!(task.childMatches > 0)) return <span className="inline-block w-4" />;
  return (
    <button
      type="button"
      aria-label={expanded ? "Alt işleri gizle" : "Alt işleri göster"}
      aria-expanded={expanded}
      onClick={(e) => {
        e.stopPropagation();
        onToggle(task._id);
      }}
      className="inline-flex items-center justify-center w-4 h-4 text-gray-500 hover:text-gray-800"
    >
      <FaChevronRight className={`w-3 h-3 transition-transform ${expanded ? "rotate-90" : ""}`} />
    </button>
  );
}

// Alt iş satırının başındaki bağlantı çizgisi (├ / └).
const Branch = ({ last }) => (
  <span className="text-gray-300 font-mono select-none" aria-hidden="true">{last ? "└" : "├"}</span>
);

// 1.8 — atananlar: can.assign ise × / + ile işi açmadan değişir (arşivdeki işte değil).
function Assignees({ task: t, onAssign }) {
  return (
    <AssigneeChips
      users={t.assignees || []}
      canEdit={Boolean(onAssign && t.can?.assign && !t.archivedAt)}
      onChange={(ids) => onAssign(t, ids, t.parent?._id)}
    />
  );
}

// child: { last } ise alt iş satırı (girintili), değilse kök satır.
function TaskRow({ task: t, onSelect, child, expanded, onToggle, onAssign }) {
  const dim = !child && t.matchesFilter === false;
  return (
    <tr
      className={`hover:bg-gray-50 transition-colors cursor-pointer ${bgFor(t, child ? "bg-gray-50/40" : "bg-white")} ${dim ? "opacity-60" : ""}`}
      onClick={() => onSelect && onSelect(t)}
      data-testid={child ? "task-child-row" : "task-row"}
    >
      <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">
        <span className="inline-flex items-center gap-1.5">
          {child ? (
            <span className="inline-flex items-center gap-1 pl-5"><Branch last={child.last} /></span>
          ) : (
            <ExpandToggle task={t} expanded={expanded} onToggle={onToggle} />
          )}
          {t.key}
        </span>
      </td>
      <td className="px-4 py-3">
        {!child && <ParentKey parent={t.parent} className="block" />}
        <span className={`text-sm text-gray-800 ${child ? "" : "font-Medium"}`}>{t.title}</span>
        <TaskMeta task={t} />
      </td>
      <td className="px-4 py-3"><StatusBadge status={t.status} /></td>
      <td className="px-4 py-3"><PriorityIcon priority={t.priority} showLabel /></td>
      <td className="px-4 py-3"><Assignees task={t} onAssign={onAssign} /></td>
      <td className="px-4 py-3 text-sm whitespace-nowrap"><DueDate task={t} /></td>
      <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">{formatDate(t.updatedAt) || "—"}</td>
    </tr>
  );
}

function TaskCard({ task: t, onSelect, child, expanded, onToggle, onAssign }) {
  const dim = !child && t.matchesFilter === false;
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onSelect && onSelect(t)}
      onKeyDown={(e) => e.key === "Enter" && onSelect && onSelect(t)}
      className={`w-full text-left border-b border-gray-100 px-3 py-3 space-y-1.5 ${child ? "pl-8" : ""} ${bgFor(t, child ? "bg-gray-50" : "bg-white")} ${dim ? "opacity-60" : ""}`}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="inline-flex items-center gap-1.5 text-xs text-gray-500">
          {child ? <Branch last={child.last} /> : <ExpandToggle task={t} expanded={expanded} onToggle={onToggle} />}
          {t.key}
        </span>
        <StatusBadge status={t.status} />
      </div>
      {!child && <ParentKey parent={t.parent} className="block" />}
      <div className="text-sm font-Medium text-gray-800">{t.title}</div>
      <TaskMeta task={t} />
      <div className="flex items-center justify-between gap-2 text-xs">
        <PriorityIcon priority={t.priority} showLabel />
        <DueDate task={t} />
      </div>
      {((t.assignees || []).length > 0 || t.can?.assign) && <Assignees task={t} onAssign={onAssign} />}
    </div>
  );
}

// 1.6 — açılan üst işin alt işleri: list { parent, …aynı filtreler }.
function Children({ parent, childArgs, sort, onSelect, mobile, onAssign }) {
  const { data, isLoading, isError } = useGetTasksQuery({ ...childArgs, sort, parent: parent._id });
  const rows = data?.data?.tasks || [];
  const note = (text) =>
    mobile ? (
      <div className="px-3 py-2 pl-8 text-xs text-gray-400 bg-gray-50">{text}</div>
    ) : (
      <tr className="bg-gray-50/40">
        <td colSpan={COLUMNS.length} className="px-4 py-2 pl-14 text-xs text-gray-400">{text}</td>
      </tr>
    );
  if (isLoading) return note("Alt işler yükleniyor...");
  if (isError) return note("Alt işler yüklenemedi.");
  if (!rows.length) return note("Filtreye uyan alt iş yok.");
  const Item = mobile ? TaskCard : TaskRow;
  return rows.map((c, i) => (
    <Item key={c._id} task={c} onSelect={onSelect} onAssign={onAssign} child={{ last: i === rows.length - 1 }} />
  ));
}

function TaskList({ tasks = [], total = 0, page = 1, onPage, sort, onSort, onSelect, loading, childArgs = {} }) {
  // Alt işler varsayılan AÇIK — her zaman üst işin altında görünür; ▾ ile yalnız
  // kapatılanlar tutulur.
  const [collapsed, setCollapsed] = useState(() => new Set());
  const onAssign = useQuickAssign();
  const toggle = (id) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const headers = COLUMNS.map((c) => {
    if (!c.field) return { key: c.key, label: c.label };
    const active = sort?.field === c.field;
    return {
      key: c.key,
      label: (
        <button
          type="button"
          onClick={() =>
            onSort &&
            onSort({
              field: c.field,
              order: active && sort.order === "asc" ? "desc" : "asc",
            })
          }
          className="inline-flex items-center gap-1 hover:text-gray-700"
        >
          {c.label}
          {active &&
            (sort.order === "asc" ? (
              <FaSortUp className="w-3 h-3" />
            ) : (
              <FaSortDown className="w-3 h-3" />
            ))}
        </button>
      ),
    };
  });

  const render = (mobile) => (t) => {
    const Item = mobile ? TaskCard : TaskRow;
    const open = !collapsed.has(t._id) && t.childMatches > 0;
    return (
      <>
        <Item task={t} onSelect={onSelect} expanded={open} onToggle={toggle} onAssign={onAssign} />
        {open && (
          <Children parent={t} childArgs={childArgs} sort={sort} onSelect={onSelect} mobile={mobile} onAssign={onAssign} />
        )}
      </>
    );
  };
  const renderRow = render(false);
  const renderMobileCard = render(true);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <ListView
      headers={headers}
      data={tasks}
      renderRow={renderRow}
      renderMobileCard={renderMobileCard}
      isLoading={loading}
      loadingNode={
        <ListSkeleton
          message="İşler yükleniyor..."
          columns={COLUMNS.map((c) => ({ label: c.label, skeletonWidth: "w-20" }))}
        />
      }
      empty={<EmptyState message="İş bulunamadı" suggestion="Filtreleri değiştirmeyi deneyin." />}
      footer={
        total > PAGE_SIZE ? (
          <div className="shrink-0 border-t border-gray-100 px-3 py-2">
            <Pagination
              currentPage={page}
              totalPages={totalPages}
              onPageChange={onPage}
              itemsPerPage={PAGE_SIZE}
              totalItems={total}
            />
          </div>
        ) : null
      }
    />
  );
}

export { PAGE_SIZE };
export default TaskList;
